import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart } from 'chart.js';
import { AnalyticsService } from '../../../../services/analytics.service';
import { Subscription } from 'rxjs';
import { getDoughnutChartConfig, PARKBOT_COLORS } from '../../../utils/chart-config';
import { AccountService } from '../../../../services/account.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-card-session-status',
  templateUrl: './card-session-status.component.html',
  styleUrls: ['./card-session-status.component.css']
})
export class CardSessionStatusComponent implements OnInit, AfterViewInit, OnDestroy {
  statusData: any;
  activeVehicleData: any = {
    counts: {},
    percentages: {}
  };
  
  vehicleTypes: any[] = [];
  account: any;
  lastUpdated: Date = new Date();
  chart: Chart;
  subscriptions: Subscription[] = [];
  currentZoneId: string;
  colorMap: Map<string, string> = new Map();
  isLoading = false;
  
  constructor(
    private analyticsService: AnalyticsService,
    private accountService: AccountService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Subscribe to loading state
    this.subscriptions.push(
      this.analyticsService.loading$.subscribe(loading => {
        this.isLoading = loading;
      })
    );

    // Start real-time Firestore updates
    this.analyticsService.startRealtimeUpdates();
    
    // Load account and vehicle types
    this.account = JSON.parse(localStorage.getItem('account'));
    
    // Get current zone ID from URL if available
    this.updateCurrentZoneId();
    
    // Listen for router events to detect zone changes
    this.subscriptions.push(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        const newZoneId = this.getZoneIdFromUrl();
        if (newZoneId !== this.currentZoneId) {
          this.currentZoneId = newZoneId;
          this.loadVehicleTypes();
        }
      })
    );
    
    // Listen for date change events from analytics service
    this.subscriptions.push(
      this.analyticsService.dateRangeChanged.subscribe(() => {
        this.loadVehicleTypes();
      })
    );
    
    // Initial load
    this.loadVehicleTypes();
  }
  
  private updateCurrentZoneId(): void {
    this.currentZoneId = this.getZoneIdFromUrl();
  }
  
  private getZoneIdFromUrl(): string {
    const urlParts = window.location.pathname.split('/');
    const zoneId = urlParts[urlParts.length - 1];
    return zoneId !== 'dashboard' ? zoneId : undefined;
  }
  
  private loadVehicleTypes(): void {
    // Get vehicle types from account settings filtered by zone if valid
    this.subscriptions.push(
      this.accountService.getVehicleTypesFromAccount(
        this.account,
        this.currentZoneId
      ).subscribe(vehicleTypes => {
        this.vehicleTypes = vehicleTypes;
        
        // Create consistent color mapping for vehicle types
        this.assignColorsToVehicleTypes();
        
        // Initialize empty active vehicle data structure
        this.initializeActiveVehicleData();
        
        // Load data now that we have vehicle types
        this.loadSessionData();
      })
    );
  }
  
  private assignColorsToVehicleTypes(): void {
    // Assign consistent colors to vehicle types
    this.colorMap.clear();
    this.vehicleTypes.forEach((vt, index) => {
      if (vt && vt.type) {
        this.colorMap.set(vt.type, PARKBOT_COLORS.colors[index % PARKBOT_COLORS.colors.length]);
      }
    });
  }
  
  private initializeActiveVehicleData(): void {
    
    // Initialize with default structure
    this.activeVehicleData = {
      counts: { total: 0 },
      percentages: {}
    };
    
    // Add vehicle types from account settings
    if (this.vehicleTypes && this.vehicleTypes.length > 0) {
      this.vehicleTypes.forEach(vt => {
        if (vt && vt.type) {
          this.activeVehicleData.counts[vt.type] = 0;
          this.activeVehicleData.percentages[vt.type] = 0;
        } else {
        }
      });
    } else {
      // Fallback to default vehicle types
      const defaultTypes = [
        'light_vehicle', 'medium_vehicle', 'heavy_vehicle',
        'motorcycle', 'tricycle', 'vip_vehicle', 'valet_vehicle'
      ];
      
      defaultTypes.forEach(type => {
        this.activeVehicleData.counts[type] = 0;
        this.activeVehicleData.percentages[type] = 0;
      });
    }
    
  }
  
  private loadSessionData(): void {
    // Stop any existing real-time updates
    this.analyticsService.stopRealtimeUpdates();
    
    // Start real-time updates with the current zone ID
    this.analyticsService.setZoneId(this.currentZoneId);
    this.analyticsService.startRealtimeUpdates(this.account?.account_key);
    
    // Get session status data
    this.subscriptions.push(
      this.analyticsService.getSessionStatusBreakdown()
        .subscribe(data => {
          this.statusData = data;
        })
    );
    
    // Get active and expired vehicles by type - this will now update in real-time
    // when the Firestore snapshot listener triggers
    this.subscriptions.push(
      this.analyticsService.getActiveVehiclesByType()
        .subscribe(data => {
          this.activeVehicleData = data;
          this.lastUpdated = new Date();
          
          // Make sure all vehicle types are present
          const predefinedCategories = [
            { key: 'light_vehicle', name: 'Car', color: '#3B82F6' },
            { key: 'motorcycle', name: 'Motorcycle', color: '#EF4444' },
            { key: 'valet_light_vehicle', name: 'Valet (Guest)', color: '#10B981' },
            { key: 'vip_light_vehicle', name: 'Doctor (Paid)', color: '#F59E0B' },
            { key: 'vip_free_light_vehicle', name: 'Doctor (Free)', color: '#8B5CF6' }
          ];
          
          predefinedCategories.forEach(category => {
            if (!this.activeVehicleData.counts[category.key] && this.activeVehicleData.counts[category.key] !== 0) {
              this.activeVehicleData.counts[category.key] = 0;
            }
            if (!this.activeVehicleData.percentages[category.key] && this.activeVehicleData.percentages[category.key] !== 0) {
              this.activeVehicleData.percentages[category.key] = 0;
            }
          });
          
          if (this.chart) {
            this.updateChart();
          } else {
            this.initChart();
          }
        })
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initChart();
    }, 100);
  }

  ngOnDestroy(): void {
    // Stop real-time updates when component is destroyed
    this.analyticsService.stopRealtimeUpdates();
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => {
      if (sub) sub.unsubscribe();
    });
    
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private initChart(): void {
    if (!this.activeVehicleData) return;
    
    const canvas = document.getElementById('session-status-chart') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Use the predefined categories with their display names
    const categories = [
      { key: 'light_vehicle', name: 'Car', color: '#3B82F6' },
      { key: 'motorcycle', name: 'Motorcycle', color: '#EF4444' },
      { key: 'valet_light_vehicle', name: 'Valet (Guest)', color: '#10B981' },
      { key: 'vip_light_vehicle', name: 'Doctor (Paid)', color: '#F59E0B' },
      { key: 'vip_free_light_vehicle', name: 'Doctor (Free)', color: '#8B5CF6' }
    ];

    let labels = [];
    let data = [];
    let colors = [];
    
    // Build chart data from predefined categories
    categories.forEach(category => {
      const count = this.activeVehicleData.counts[category.key] || 0;
      if (count > 0) { // Only include categories with data
        labels.push(category.name);
        data.push(count);
        colors.push(category.color);
      }
    });
    
    // Filter out vehicle types with no active vehicles
    // If no data, show empty chart with message
    if (labels.length === 0) {
      labels = ['No Active Vehicles'];
      data = [1];
      colors = ['#f0f0f0']; // Light gray for "no data"
      
      const datasets = [
        {
          data,
          backgroundColor: colors,
          borderWidth: 0
        }
      ];
      
      const config = getDoughnutChartConfig(labels, datasets);
      config.options.plugins.tooltip = {
        ...config.options.plugins.tooltip,
        callbacks: {
          label: () => 'No active vehicles'
        }
      } as any;
      
      this.chart = new Chart(ctx, config);
      return;
    }
    
    const datasets = [
      {
        data,
        backgroundColor: colors,
        borderWidth: 0
      }
    ];

    const config = getDoughnutChartConfig(labels, datasets);
    
    // Add custom tooltip to show counts and percentages
    config.options.plugins.tooltip = {
      ...config.options.plugins.tooltip,
      callbacks: {
        label: (context) => {
          const label = context.label || '';
          const value = context.raw || 0;
          const total = this.activeVehicleData.counts.total;
          const percentage = total > 0 ? Math.round(((value as number) / total) * 100) : 0;
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    } as any;
    
    this.chart = new Chart(ctx, config);
  }

  private updateChart(): void {
    if (!this.chart || !this.activeVehicleData) return;
    
    // Use the predefined categories with their display names
    const categories = [
      { key: 'light_vehicle', name: 'Car', color: '#3B82F6' },
      { key: 'motorcycle', name: 'Motorcycle', color: '#EF4444' },
      { key: 'valet_light_vehicle', name: 'Valet (Guest)', color: '#10B981' },
      { key: 'vip_light_vehicle', name: 'Doctor (Paid)', color: '#F59E0B' },
      { key: 'vip_free_light_vehicle', name: 'Doctor (Free)', color: '#8B5CF6' }
    ];

    let labels = [];
    let data = [];
    let colors = [];
    
    // Build chart data from predefined categories
    categories.forEach(category => {
      const count = this.activeVehicleData.counts[category.key] || 0;
      if (count > 0) { // Only include categories with data
        labels.push(category.name);
        data.push(count);
        colors.push(category.color);
      }
    });
    
    // If no data after filtering, handle empty state
    if (labels.length === 0) {
      this.chart.data.labels = ['No Active Vehicles'];
      this.chart.data.datasets[0].data = [1];
      this.chart.data.datasets[0].backgroundColor = ['#f0f0f0'];
    } else {
      // Update chart with filtered data
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = data;
      this.chart.data.datasets[0].backgroundColor = colors;
    }
    
    this.chart.update();
  }
}
